import { computed } from 'vue';
import { sanitize } from '@vue-util/sanitize';
import safeType from '@utils/safeType';
import { useProduct } from '@modules/product';

/** @typedef {{hasReviews: true, count: number, rating: number} | {hasReviews: false}} ReviewDetails */
/** @typedef {{productDetails: string[], sizeAndFit: string[], materialsAndCare: string[] }} AdditionalDescriptions */

/**
 * @param {DetailReviewStatistics} reviewStatistics
 * @returns {ReviewDetails}
 */
function getReviewStatistics(reviewStatistics) {
  if (reviewStatistics.aggregate) {
    return {
      hasReviews: true,
      // below removing ratings-only count to follow number used for main review section
      count: reviewStatistics.aggregate.count - reviewStatistics.aggregate.ratingsOnlyReviewCount,
      rating: reviewStatistics.aggregate.rating,
    };
  }
  return { hasReviews: false };
}

/**
 *
 * @param {Detail} detailData
 * @returns {AdditionalDescriptions}
 */
function getProcessedProductDescription(detailData) {
  if (detailData.processedProdDesc) {
    return {
      productDetails: detailData.processedProdDesc.productDetails,
      sizeAndFit: detailData.processedProdDesc.sizeAndFit,
      materialsAndCare: detailData.materialsAndCare || detailData.processedProdDesc.fabricAndCare,
    };
  }
  return {
    productDetails: [],
    sizeAndFit: [],
    materialsAndCare: detailData.materialsAndCare || [],
  };
}

/**
 * Computes product details based on the provided data.
 *
 * @param {Detail} detailData
 * @returns {{
 * productName: import('vue').ComputedRef<Detail['name']>,
 * productDescription: import('vue').ComputedRef<Detail['description']>,
 * secondaryDescription: import('vue').ComputedRef<Detail['secondaryDescription']>,
 * brandName: import('vue').ComputedRef<Detail['brand']['name']>,
 * brandDisplayName: import('vue').ComputedRef<Detail['brand']['subBrand']> | import('vue').ComputedRef<Detail['brand']['name']>,
 * brandUrl: import('vue').ComputedRef<Detail['brand']['url']>,
 * bulletText: import('vue').ComputedRef<Detail['bulletText']>,
 * bulletLinks: import('vue').ComputedRef<Detail['bulletLinks']>,
 * dimensionsBulletText: import('vue').ComputedRef<string[]>,
 * dimensionsHeaderText: import('vue').ComputedRef<Detail['dimensionsHeaderText']>,
 * processedProdDesc: import('vue').ComputedRef<AdditionalDescriptions>,
 * reviewStatistics: import('vue').ComputedRef<ReviewDetails>,
 * numberOfColors: Detail['numberOfColors'],
 * typeName: import('vue').ComputedRef<Detail['typeName']>,
 * swatchEmail: Detail['swatchEmail'],
 * metric: Metric,
 * pdfEmailDescription: import('vue').ComputedRef<Detail['pdfEmailDescription']>,
 * }}
 */
export function useProductDetails(detailData) {
  return {
    productName: computed(() => detailData.name || ''),
    productDescription: computed(() => sanitize(detailData.description) || ''),
    secondaryDescription: computed(() => detailData.secondaryDescription || ''),
    brandName: computed(() => detailData.brand?.name || ''),
    brandDisplayName: computed(() => sanitize(detailData.brand?.subBrand) || sanitize(detailData.brand?.name) || ''),
    brandUrl: computed(() => detailData.brand?.url || ''),
    bulletText: computed(() => detailData.bulletText || []),
    bulletLinks: computed(() => detailData.bulletLinks || []),
    dimensionsBulletText: computed(() => detailData.dimensionsBulletText || []),
    dimensionsHeaderText: computed(() => detailData.dimensionsHeaderText || ''),
    processedProdDesc: computed(() => getProcessedProductDescription(detailData)),
    reviewStatistics: computed(() => getReviewStatistics(detailData.reviewStatistics)),
    numberOfColors: detailData.numberOfColors || 0,
    typeName: computed(() => detailData.typeName || ''),
    swatchEmail: detailData.swatchEmail || '',
    metric: detailData.metric || { productUnitSalesCount: '', productUnitSalesCountMessage: '' },
    pdfEmailDescription: computed(() => detailData.pdfEmailDescription || ''),
  };
}

/**
 * Computes shipping details based on the provided data.
 *
 * @param {Shipping} shippingData
 * @returns {{
 * isShippingAvailable: import('vue').ComputedRef<boolean>,
 * freeShippingMessages: import('vue').ComputedRef<Shipping['freeShippingMessages']>,
 * notes: import('vue').ComputedRef<Shipping['notes']>,
 * returnConstraintMessage: import('vue').ComputedRef<Shipping['returnConstraintMessage']>,
 * stateRestrictionMessage: import('vue').ComputedRef<Shipping['stateRestrictionMessage']>,
 * shippingMessage: import('vue').ComputedRef<Shipping['shippingMessage']>,
 * specialGiftWrapMessage: import('vue').ComputedRef<Shipping['specialGiftWrapMessage']>,
 * giftWrapMessage: import('vue').ComputedRef<Shipping['giftWrapMessage']>,
 * surchargeMessage: import('vue').ComputedRef<Shipping['surchargeMessage']>,
 * marketPlaceProductShippingMessages: import('vue').ComputedRef<Shipping['marketPlaceProductShippingMessages']>,
 * bigTicketDeliveryMethods: import('vue').ComputedRef<Shipping['bigTicketDeliveryMethods']>,
 * }}
 */
export function useShippingDetails(shippingData) {
  return {
    isShippingAvailable: computed(() => Object.keys(shippingData).length > 0),
    notes: computed(() => shippingData.notes || []),
    freeShippingMessages: computed(() => shippingData.freeShippingMessages || []),
    marketPlaceProductShippingMessages: computed(() => shippingData.marketPlaceProductShippingMessages || []),
    returnConstraintMessage: computed(() => sanitize(shippingData.returnConstraintMessage) || ''),
    stateRestrictionMessage: computed(() => sanitize(shippingData.stateRestrictionMessage) || ''),
    giftWrapMessage: computed(() => sanitize(shippingData.giftWrapMessage) || ''),
    specialGiftWrapMessage: computed(() => sanitize(shippingData.specialGiftWrapMessage) || ''),
    surchargeMessage: computed(() => sanitize(shippingData.surchargeMessage) || ''),
    shippingMessage: computed(() => sanitize(shippingData.shippingMessage) || ''),
    bigTicketDeliveryMethods: computed(() => shippingData.bigTicketDeliveryMethods || []),
  };
}

/**
 * Computes relationships details based on the provided data.
 *
 * @param {Relationships} relationshipsData
 * @returns {{
 * taxonomy: import('vue').ComputedRef<Relationships['taxonomy']>,
 * }}
 */
export function useRelationshipsDetails(relationshipsData) {
  return {
    taxonomy: computed(() => relationshipsData.taxonomy || {}),
  };
}

/**
 * Computes pricing badges details based on the provided data.
 *
 * @param {Pricing} pricingData
 * @returns {{
 * badgesMap: import('vue').ComputedRef<Pricing['badgesMap']>,
 * }}
 */
export function usePricingBadgesDetails(pricingData) {
  return {
    badgesMap: computed(() => pricingData.badgesMap || {}),
  };
}

/**
 * @param {Product} [product]
 * @returns {{ productUrl: string, topLevelCategoryID: string, topLevelCategoryName: string }}
 */
export function useIdentifier(product) {
  if (product) {
    return {
      productUrl: product.identifier.productUrl,
      topLevelCategoryID: product.identifier.topLevelCategoryID,
      topLevelCategoryName: safeType(product.identifier.topLevelCategoryName, ''),
    };
  }
  return {
    productUrl: '',
    topLevelCategoryID: '',
    topLevelCategoryName: '',
  };
}

// TODO: Provide/inject ???
// TODO: remove all computed
export function useCommonProductGetters() {
  const { productData, productId } = useProduct();
  const isProductAvailable = !!productData?.availability?.available;
  const isCcfpAvailable = !!productData?.ccfp?.ccfpEligible;

  const productDetails = useProductDetails(productData?.detail || {});
  const shippingDetails = useShippingDetails(productData?.shipping || {});
  const relationshipsDetails = useRelationshipsDetails(productData?.relationships || {});
  const pricingBadgesDetails = usePricingBadgesDetails(productData?.pricing || {});
  const identifier = useIdentifier(productData);
  const bannerDetails = computed(() => productData?.banner || {});
  const isSingleUpcProduct = computed(() => Object.keys(productData?.traits?.traitsMaps?.upcMap || {}).length === 1);
  /**
   * @param {(value: Product, index: number, array: any[]) => unknown} cb
   * @returns {boolean}
   */
  function anyMemberProducts(cb) {
    return Object.values(productData?.relationships?.memberProductMap || {}).some(cb);
  }

  return {
    productId,
    isProductAvailable,
    isCcfpAvailable,
    bannerDetails,
    ...identifier,
    ...productDetails,
    ...shippingDetails,
    ...relationshipsDetails,
    ...pricingBadgesDetails,
    anyMemberProducts,
    isSingleUpcProduct,
  };
}

export default useCommonProductGetters;
